// docs/51 friendly villages: what the village view draws. The screen (`VillageScreen.tsx`) reads the
// module's `village` / `village_npc` rows into these shapes; the view (`VillageView.tsx`) paints
// them with no server (the `?village=1` harness feeds it a fixed village).
import type { SeasonKey } from '../../../core/story/calendar';
import type { OwWeather } from '../map/worldSky';
import type { VillageFigure } from '../../../bindings/types';
import type { HeroLook } from '../../../game/heroKit';

/** docs/112 UP6: the workshops a village may have, in `fk_grammar::UpgradeShop` order (docs/142: the dyer last). */
export type WorkshopKind = 'enchanter' | 'hedgewitch' | 'runecarver' | 'stargazer' | 'weaponsmith' | 'armourer' | 'bowyer' | 'leatherworker' | 'dyer';
/** The eight upgrade workshops (spots 5–12). */
export const WORKSHOP_KINDS: readonly WorkshopKind[] = ['enchanter', 'hedgewitch', 'runecarver', 'stargazer', 'weaponsmith', 'armourer', 'bowyer', 'leatherworker'];
/** Where in a village the party stands: the street, or inside one of its buildings (docs/116 CI16: or the band's room upstairs at the inn). */
export type VillageSpot = 'street' | 'inn' | 'shop' | 'smithy' | 'temple' | WorkshopKind | 'upstairs';
/** The order the module numbers spots in (`village_presence.spot`): its `Building`s, the workshops, the room upstairs, then
 *  (docs/142 DY5) the dyer's yard. `fk-world` checks the dyer is last (`dyeing.rs`). */
export const SPOT_ORDER: readonly VillageSpot[] = ['street', 'inn', 'shop', 'smithy', 'temple', ...WORKSHOP_KINDS, 'upstairs', 'dyer'];
/** The four buildings every other one is drawn like, outside and in. */
export type BuildingStyle = 'inn' | 'shop' | 'smithy' | 'temple';
/** docs/112: a workshop is drawn in the style of the building its trade is nearest (a forge for the
 *  smiths, a chapel-like tower for the enchanter and stargazer, a cottage for the witch and tailor). */
export const STYLE_OF: Record<Exclude<VillageSpot, 'street'>, BuildingStyle> = {
  inn: 'inn', shop: 'shop', smithy: 'smithy', temple: 'temple', upstairs: 'inn',
  enchanter: 'temple', stargazer: 'temple', runecarver: 'smithy', weaponsmith: 'smithy', armourer: 'smithy',
  hedgewitch: 'inn', bowyer: 'shop', leatherworker: 'shop', dyer: 'shop',
};
export const isWorkshop = (s: VillageSpot): s is WorkshopKind => s === 'dyer' || (WORKSHOP_KINDS as readonly string[]).includes(s);
/** A building's door on the street (`street` is never a building). */
export type BuildingKind = Exclude<VillageSpot, 'street'>;
/** How big the place is: a hamlet has an inn and a shop; a village adds the smithy; a town the temple too. */
export type VillageSize = 'hamlet' | 'village' | 'town';

export interface VillageBuilding { kind: BuildingKind; name: string }

export interface VillageLook {
  /** Seeds the street's layout (which house stands where, the trees, the props). */
  seed: number;
  name: string;
  size: VillageSize;
  /** The buildings with doors, in the order they stand along the street (the inn first). */
  buildings: VillageBuilding[];
  /** The realm's country (`clock.biome`: temperate, wetland, boreal, desert, highland, jungle, underdark, ashlands). */
  biome?: string;
  season?: SeasonKey;
  weather?: OwWeather;
  /** After dark: lit windows, lanterns, stars (one World week in three, `townBand`). */
  night: boolean;
  /** The haven (the World's seat): the quest board stands in its square. */
  haven?: boolean;
}

/** What a villager offers beyond talk: to join the band, a secret for coin, a trade. */
export type DealKind = 'hire' | 'secret' | 'trade';

export interface VillageNpc {
  key: string;
  name: string;
  /** "the innkeeper", "a sellsword between contracts". */
  epithet: string;
  /** Oryx creature frame (frame A; frame A + 18 is the off beat, as everywhere). */
  frame: number;
  /** Where they are found. */
  at: VillageSpot;
  deal?: DealKind;
  /** They have something to say about a quest on the board (a "!" over their head). */
  quest?: boolean;
  /** A trouble has sprung here and they are the one crying out (a pulsing "!"). */
  alarm?: boolean;
  /** docs/64 VP7: what their deal costs, shown before anyone asks. */
  price?: number;
}

/** One of my heroes, standing in the village with the band. */
/** `id`/`mine` let the street show the hero's emotes (docs/62). */
export interface VillageHero {
  key: string; name: string; frame: number; classId: string; id?: bigint; mine?: boolean;
  /** docs/145: a stand-in's kit look (a real hero's comes from the store by `id`) */ look?: HeroLook;
  /** a ranger's beast (docs/59), walking at its ranger's heel */ companion?: boolean;
  /** the beast's companion id (docs/62): its pet emotes */ petId?: bigint;
  /** docs/73 TP2: another player's hero: whose it is, and where that player's band stands (`village_presence`) */
  owner?: string; stand?: VillageStand;
  /** docs/73 TP3: just hired: steps out of where this villager stood and falls in behind the band */
  from?: string;
}
/** Where a player's band leader stands in the view (cells; x < 0 = not walked anywhere yet), and whether the band sits at the table. */
/** docs/116 CI3: the pose is any of the inn's (`inn/types.ts POSE`): 0 standing, 1 at the meal table, 2–7 the hearth … asleep. */
export interface VillageStand { x: number; y: number; pose: number }

/** docs/64 VP2: walk the band over to a villager, then talk (`n` makes a repeat request new). */
export interface VillageSummon { key: string; n: number }

/** docs/64 VP4: a meal at the inn as the view plays it. */
export type MealStage = 'seating' | 'serving' | 'eating' | 'resting';
/** The meal the band has ordered (`n` makes each order new); `diners` are the hero keys at the table. */
export interface VillageMeal { n: number; diners: string[] }

/** docs/120 VM2: another player's figures in the room I am in (their `village_figures` row; `atMs` server time). */
export interface RemoteFigures { owner: string; atMs: number; figures: VillageFigure[]; talkNpc?: string }
/** docs/120 VM4: a seat or station in this room someone holds (`village_seat`; codes in `sync.ts` SEAT). */
export interface HeldSeat { seat: number; owner: string; heroId: bigint }
